import { useState, useEffect, useCallback } from "react";
import { FiSearch, FiArrowLeft, FiChevronRight, FiCheck, FiX, FiPrinter } from "react-icons/fi";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";
import { PageHeader, Card, Pill, Btn, THead, TD, Icon } from "./KioskUI";
import BulkImport from "./BulkImport";
import { RestockInvoiceDetail } from "./RestockInvoices";

const MMK = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (s) => new Date(s).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
const fmtPoNo = (id) => `PO-${String(id).padStart(5, "0")}`;

const SUPPLIER_IMPORT_COLUMNS = [
  { key: "name", label: "Name", required: true },
  { key: "contact", label: "Contact" },
  { key: "phone", label: "Phone" },
];


function stockStatus(p) {
  if (p.quantity === 0) return "out";
  if (p.quantity <= p.low_stock_threshold) return "low";
  return "ok";
}
const STATUS_LABEL = { ok: "In stock", low: "Low", out: "Out" };

/* ─── Add / Edit modal ─── */
function SupplierModal({ existing, token, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    name: existing?.name || "",
    contact: existing?.contact || "",
    phone: existing?.phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canSave = form.name.trim().length > 0 && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true); setError("");
    try {
      const payload = { name: form.name.trim(), contact: form.contact.trim(), phone: form.phone.trim() };
      const res = isEdit
        ? await apiPut(`/api/inventory/suppliers/${existing.id}`, payload, token)
        : await apiPost("/api/inventory/suppliers", payload, token);
      if (res.ok) { onSaved(await res.json(), !isEdit); }
      else { const err = await res.json().catch(() => ({})); setError(err.error || "Failed to save."); }
    } catch { setError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <form onClick={e => e.stopPropagation()} onSubmit={handleSubmit}
        style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 480, maxWidth: "94vw", padding: "24px 26px 22px", display: "flex", flexDirection: "column" }}>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
            <div style={{ width: 44, height: 44, borderRadius: 13, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="suppliers" size={21} stroke="var(--primary)" />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
                <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>
                  {isEdit ? "Edit Supplier" : "Add Supplier"}
                </span>
                <span style={{ fontFamily: "var(--font-mm)", fontSize: 14, color: "var(--n-400)" }}>
                  {isEdit ? "ပေးသွင်းသူပြင်ဆင်" : "ပေးသွင်းသူအသစ်"}
                </span>
              </div>
              <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 3 }}>A factory or importer you buy stock from.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 7, cursor: "pointer", display: "flex", flexShrink: 0 }}>
            <FiX size={16} color="var(--sub)" />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <MField label="Supplier name" mm="အမည်" required style={{ gridColumn: "1 / -1" }}>
            <input autoFocus value={form.name} onChange={e => set("name", e.target.value)}
              placeholder="e.g. Delta Textile Factory" style={inpSt} />
          </MField>
          <MField label="Contact person">
            <input value={form.contact} onChange={e => set("contact", e.target.value)}
              placeholder="e.g. U Zaw Win" style={inpSt} />
          </MField>
          <MField label="Phone">
            <input value={form.phone} onChange={e => set("phone", e.target.value)}
              placeholder="09 …" style={inpSt} />
          </MField>
        </div>

        {error && (
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Btn kind="ghost" onClick={onClose} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button type="submit" disabled={!canSave}
            style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: canSave ? "pointer" : "not-allowed", background: canSave ? "var(--primary)" : "var(--n-200)", color: "#fff" }}>
            <FiCheck size={16} /> {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Supplier"}
          </button>
        </div>
      </form>
    </div>
  );
}

function MField({ label, mm, required, style, children }) {
  return (
    <div style={style}>
      <label style={{ display: "flex", gap: 7, alignItems: "baseline", fontSize: 12.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 7 }}>
        {label}
        {required && <span style={{ color: "var(--danger)" }}>·</span>}
        {mm && <span style={{ fontFamily: "var(--font-mm)", fontWeight: 500, color: "var(--n-400)" }}>{mm}</span>}
      </label>
      <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "10px 13px", background: "var(--surface)" }}>
        {children}
      </div>
    </div>
  );
}
const inpSt = { border: "none", outline: "none", background: "transparent", width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)" };

/* ─── Delete confirm ─── */
function DeleteConfirm({ supplier, onCancel, onConfirm, busy }) {
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 380, padding: "26px 26px 22px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 13, marginBottom: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "var(--danger-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="trash" size={20} stroke="var(--danger)" />
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>Delete "{supplier.name}"?</div>
            <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 4, lineHeight: 1.5 }}>
              This supplier will be removed. Past receiving records are kept.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <Btn kind="ghost" onClick={onCancel} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button onClick={onConfirm} disabled={busy}
            style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: busy ? "not-allowed" : "pointer", background: busy ? "var(--n-200)" : "var(--danger)", color: "#fff" }}>
            <Icon name="trash" size={16} stroke="#fff" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Supplier detail ─── */
function SupplierDetail({ supplierId, token, go, onBack, onSaved, onDeleted }) {
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [restocks, setRestocks] = useState([]);
  const [restocksLoading, setRestocksLoading] = useState(true);
  const [viewingInvoice, setViewingInvoice] = useState(null); // restock_id
  const [form, setForm]         = useState(null);
  const [saving, setSaving]     = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    Promise.all([
      apiGet(`/api/inventory/suppliers/${supplierId}`, token).then(r => r.json()),
      apiGet(`/api/inventory/products?supplier_id=${supplierId}`, token).then(r => r.json()),
    ]).then(([s, prods]) => {
      setSupplier(s);
      setForm({ name: s.name, contact: s.contact || "", phone: s.phone || "" });
      setProducts(Array.isArray(prods) ? prods : []);
    }).catch(console.error);
  }, [supplierId, token]);

  useEffect(() => {
    setRestocksLoading(true);
    apiGet(`/api/inventory/restocks?supplier_id=${supplierId}`, token)
      .then(r => r.json())
      .then(d => setRestocks(Array.isArray(d) ? d : []))
      .catch(() => setRestocks([]))
      .finally(() => setRestocksLoading(false));
  }, [supplierId, token]);

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const res = await apiPut(`/api/inventory/suppliers/${supplierId}`, form, token);
      if (res.ok) {
        const updated = await res.json();
        setSupplier(updated);
        onSaved(updated);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || "Failed to save.");
      }
    } catch { setError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await apiDelete(`/api/inventory/suppliers/${supplierId}`, token);
      if (res.ok) { onDeleted(supplierId); }
      else { const err = await res.json().catch(() => ({})); setError(err.error || "Failed to delete."); setDelConfirm(false); }
    } catch { setError("Cannot connect to server."); setDelConfirm(false); }
    finally { setSaving(false); }
  };

  if (!supplier || !form) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
      <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  if (viewingInvoice) {
    return (
      <RestockInvoiceDetail
        restockId={viewingInvoice}
        token={token}
        onBack={() => setViewingInvoice(null)}
      />
    );
  }

  const firstName = supplier.name.split(" ")[0];
  const totalPurchased = restocks.reduce((s, r) => s + Number(r.total_amount), 0);

  return (
    <div>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <button onClick={onBack} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 8, cursor: "pointer", display: "flex" }}>
          <FiArrowLeft size={18} color="var(--sub)" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 25, fontWeight: 800, letterSpacing: "-.025em", margin: 0, color: "var(--ink)" }}>{supplier.name}</h1>
          <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 3 }}>
            #{supplier.id}{supplier.contact ? ` · ${supplier.contact}` : ""}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 16, alignItems: "start" }}>
        {/* left */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* editable details */}
          <Card style={{ padding: 24 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 18 }}>Supplier details</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <DetailField label="Supplier name" mm="အမည်" style={{ gridColumn: "1 / -1" }}>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inpSt} />
              </DetailField>
              <DetailField label="Contact person">
                <input value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} placeholder="—" style={inpSt} />
              </DetailField>
              <DetailField label="Phone">
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="—" style={inpSt} />
              </DetailField>
            </div>
            {error && (
              <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>{error}</div>
            )}
          </Card>

          {/* products supplied */}
          <Card style={{ overflow: "hidden" }}>
            <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--hair)", display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>Products supplied</div>
              <span style={{ fontFamily: "var(--font-mm)", fontSize: 13.5, color: "var(--n-400)" }}>ပစ္စည်းများ</span>
              <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--sub)" }}>{products.length} items</span>
            </div>
            {products.length === 0 ? (
              <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--n-400)", fontSize: 13.5 }}>
                No products linked to this supplier yet.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <THead cols={[{ t: "Product" }, { t: "SKU" }, { t: "Stock", r: 1 }, { t: "Status", r: 1 }]} />
                <tbody>
                  {products.map(p => {
                    const st = stockStatus(p);
                    return (
                      <tr key={p.id}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <TD b>{p.name}</TD>
                        <TD mut>{p.sku || "—"}</TD>
                        <TD r>{p.quantity}</TD>
                        <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                          <Pill tone={st}>{STATUS_LABEL[st]}</Pill>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>

          {/* purchase history */}
          <Card style={{ overflow: "hidden" }}>
            <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--hair)", display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>Purchase history</div>
              <span style={{ fontFamily: "var(--font-mm)", fontSize: 13.5, color: "var(--n-400)" }}>ဝယ်ယူမှု</span>
              <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--sub)" }}>{restocks.length} restock{restocks.length !== 1 ? "s" : ""}</span>
            </div>
            {restocksLoading ? (
              <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--sub)", fontSize: 13.5 }}>Loading…</div>
            ) : restocks.length === 0 ? (
              <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--n-400)", fontSize: 13.5 }}>
                No restocks from {firstName} yet.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <THead cols={[{ t: "PO #" }, { t: "Date" }, { t: "Items", r: 1 }, { t: "Total", r: 1 }, { t: "", r: 1, w: 30 }]} />
                <tbody>
                  {restocks.map(r => (
                    <tr key={r.restock_id} style={{ cursor: "pointer" }}
                      onClick={() => setViewingInvoice(r.restock_id)}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <TD b>{fmtPoNo(r.restock_id)}</TD>
                      <TD mut>{fmtDate(r.date)}</TD>
                      <TD r mut>{r.items_count}</TD>
                      <TD r b>{MMK(r.total_amount)} Ks</TD>
                      <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                        <FiPrinter size={14} color="var(--n-300)" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        {/* right */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* summary */}
          <Card style={{ padding: 24 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>Summary</div>
            <div style={{ fontSize: 12.5, color: "var(--sub)", marginBottom: 14 }}>{products.length} product{products.length !== 1 ? "s" : ""} on record</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderTop: "1px solid var(--hair)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13.5, color: "var(--sub)" }}>
                <span style={{ width: 8, height: 8, borderRadius: 3, background: "var(--primary)" }} /> Total purchased
              </span>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{MMK(totalPurchased)} Ks</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderTop: "1px solid var(--hair)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13.5, color: "var(--sub)" }}>
                <span style={{ width: 8, height: 8, borderRadius: 3, background: "var(--primary)" }} /> Products supplied
              </span>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{products.length}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 12, marginTop: 4 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>Balance payable</span>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--n-400)", fontVariantNumeric: "tabular-nums" }}>
                Settled
              </span>
            </div>
          </Card>

          {/* actions */}
          <div style={{ display: "flex", gap: 11 }}>
            <Btn kind="ghost" onClick={onBack} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
            <Btn kind="pri" icon="check" onClick={handleSave} disabled={saving} style={{ flex: 1, justifyContent: "center" }}>
              {saving ? "Saving…" : "Save"}
            </Btn>
          </div>
          <Btn kind="acc" icon="restock" onClick={() => go("restock")} style={{ justifyContent: "center" }}>
            Receive stock from {firstName}
          </Btn>
          <Btn kind="danger" icon="trash" onClick={() => setDelConfirm(true)} style={{ justifyContent: "center" }}>
            Delete supplier
          </Btn>
        </div>
      </div>

      {delConfirm && (
        <DeleteConfirm supplier={supplier} busy={saving} onCancel={() => setDelConfirm(false)} onConfirm={handleDelete} />
      )}
    </div>
  );
}

function DetailField({ label, mm, style, children }) {
  return (
    <div style={style}>
      <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", display: "flex", gap: 7, alignItems: "baseline", marginBottom: 7 }}>
        {label}{mm && <span style={{ fontFamily: "var(--font-mm)", fontWeight: 500, color: "var(--n-400)" }}>{mm}</span>}
      </label>
      <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "11px 13px", background: "var(--surface)" }}>
        {children}
      </div>
    </div>
  );
}

/* ─── Supplier list ─── */
export default function Suppliers({ token, go }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [q, setQ]                 = useState("");
  const [adding, setAdding]       = useState(false);
  const [importing, setImporting] = useState(false);
  const [view, setView]           = useState("list");
  const [selectedId, setSelectedId] = useState(null);

  const fetchSuppliers = useCallback(async () => {
    try {
      const r = await apiGet("/api/inventory/suppliers", token);
      const d = await r.json();
      setSuppliers(Array.isArray(d) ? d : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const openDetail = (id) => { setSelectedId(id); setView("detail"); };
  const backToList = () => { setView("list"); setSelectedId(null); };

  const handleAdded   = (s) => { setSuppliers(prev => [s, ...prev]); setAdding(false); openDetail(s.id); };
  const handleSaved   = (s) => setSuppliers(prev => prev.map(x => x.id === s.id ? s : x));
  const handleDeleted = (id) => { setSuppliers(prev => prev.filter(x => x.id !== id)); backToList(); };

  if (view === "detail") {
    return (
      <SupplierDetail
        supplierId={selectedId}
        token={token}
        go={go}
        onBack={backToList}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    );
  }

  const qq   = q.trim().toLowerCase();
  const list = suppliers.filter(s =>
    !qq || s.name.toLowerCase().includes(qq) ||
    (s.contact || "").toLowerCase().includes(qq) ||
    (s.phone || "").replace(/\s/g, "").includes(qq.replace(/\s/g, ""))
  );

  return (
    <div>
      <PageHeader title="Suppliers" mm="ပေးသွင်းသူ" desc={`${suppliers.length} supplier${suppliers.length !== 1 ? "s" : ""}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 13px", width: 280 }}>
          <FiSearch size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search suppliers…"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--ink)", width: "100%" }} />
        </div>
        <Btn kind="ghost" onClick={() => setImporting(true)}>Import</Btn>
        <Btn kind="pri" icon="plus" onClick={() => setAdding(true)}>Add Supplier</Btn>
      </PageHeader>

      {importing && (
        <BulkImport
          title="Suppliers"
          columns={SUPPLIER_IMPORT_COLUMNS}
          endpoint="/api/inventory/suppliers/bulk"
          token={token}
          onClose={() => setImporting(false)}
          onDone={fetchSuppliers}
        />
      )}

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
          <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
          {list.length === 0 && (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", color: "var(--n-400)", fontSize: 14, padding: "48px 0" }}>
              {qq ? `No suppliers match "${q}".` : "No suppliers yet."}
            </div>
          )}
          {list.map(s => (
            <SupplierCard key={s.id} supplier={s} onClick={() => openDetail(s.id)} />
          ))}
        </div>
      )}

      {adding && (
        <SupplierModal token={token} onClose={() => setAdding(false)} onSaved={handleAdded} />
      )}
    </div>
  );
}

function SupplierCard({ supplier, onClick }) {
  const [hover, setHover] = useState(false);
  const subtitle = [supplier.contact, supplier.phone].filter(Boolean).join(" · ");
  return (
    <Card
      style={{ padding: "20px 22px", cursor: "pointer", borderColor: hover ? "var(--blue-100)" : "var(--line)", background: hover ? "var(--faint)" : "var(--surface)", transition: "border-color .12s, background .12s" }}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{ width: 46, height: 46, borderRadius: 13, background: "var(--n-100)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name="suppliers" size={21} stroke="var(--n-600)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 16.5, fontWeight: 700, color: "var(--ink)" }}>{supplier.name}</div>
          <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 3 }}>{subtitle || "No contact info"}</div>
        </div>
        <FiChevronRight size={18} color="var(--n-300)" />
      </div>
    </Card>
  );
}
